import ApiError from '@Infra/FetchLib/ApiError';
import * as Fetcher from '@Infra/FetchLib/fetch';
import { refreshToken } from '@Infra/FetchLib/fetch';

type Options = {
	headers?: Record<string, string>;
	/**
	 * Whether a 401 triggers a token refresh and a retry. Defaults to `true` for every call.
	 * Set to `false` for a session probe (login, second-factor check, "who am I") where a 401 is
	 * an expected answer meaning "no session", not an expired token to refresh.
	 */
	retryOnUnauthorized?: boolean;
};

const get = <T>(url: string, options?: Options): Promise<T> => {
	return call(
		url,
		{
			method: 'GET',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				accept: 'application/json',
				'Accept-Language': 'fr',
				...options?.headers,
			},
		},
		options,
	);
};

const getPaginated = async <
	T extends {
		totalRecords: number;
		results: unknown[];
	},
>(
	url: string,
	options: Options & { page: number; rowsPerPage: number },
): Promise<T> => {
	if (options.page > 1) {
		throw new Error('You can only request the first page, in order to have all pages');
	}

	const newUrl = new URL(url);
	newUrl.searchParams.append('page', String(options.page));
	newUrl.searchParams.append('rows', String(options.rowsPerPage));

	let currentPage = options.page;
	const pageSize = options.rowsPerPage;

	const result = await get<T>(newUrl.toString(), options);

	if (result.totalRecords <= pageSize) {
		return Promise.resolve(result);
	}

	const totalPages = Math.ceil(result.totalRecords / pageSize);

	currentPage++;
	while (currentPage < totalPages) {
		newUrl.searchParams.set('page', String(currentPage));

		const payload = await get<T>(newUrl.toString(), options);

		result.results.push(...payload.results);
		currentPage++;
	}

	return Promise.resolve(result);
};

type FileResponse = {
	blob: Blob;
	filename: string | null;
};

const getFile = (url: string, options?: Options): Promise<FileResponse> => {
	return callFile(
		url,
		{
			method: 'GET',
			credentials: 'include',
			headers: {
				'Accept-Language': 'fr',
				accept: 'octet-stream',
				...options?.headers,
			},
		},
		options,
	);
};

const postFile = (url: string, body: Record<string, unknown>, options?: Options): Promise<FileResponse> => {
	return callFile(
		url,
		{
			method: 'POST',
			credentials: 'include',
			headers: {
				'Accept-Language': 'fr',
				'Content-Type': 'application/json',
				...options?.headers,
			},
			body: JSON.stringify(body),
		},
		options,
	);
};

const post = <T>(url: string, body: Record<string, unknown>, options?: Options): Promise<T> => {
	return call(
		url,
		{
			method: 'POST',
			credentials: 'include',
			headers: {
				'Accept-Language': 'fr',
				'Content-Type': 'application/json',
				accept: 'application/json',
				...options?.headers,
			},
			body: JSON.stringify(body),
		},
		options,
	);
};

function postFormData(
	url: string,
	body: FormData,
	options: Options & { responseType: 'file' },
): Promise<FileResponse>;
function postFormData<T>(
	url: string,
	body: FormData,
	options?: Options & { responseType?: 'json' },
): Promise<T>;
function postFormData<T>(
	url: string,
	body: FormData,
	options?: Options & { responseType?: 'json' | 'file' },
): Promise<T | FileResponse> {
	const headers: Record<string, string> = {
		'Accept-Language': 'fr',
		...options?.headers,
	};

	if (options?.responseType !== 'file') {
		headers.accept = 'application/json';
	}

	const init: RequestInit = {
		method: 'POST',
		credentials: 'include',
		headers,
		body,
	};

	if (options?.responseType === 'file') {
		return callFile(url, init, options);
	}

	return call(url, init, options);
}

function putFormData(
	url: string,
	body: FormData,
	options: Options & { responseType: 'file' },
): Promise<FileResponse>;
function putFormData<T>(
	url: string,
	body: FormData,
	options?: Options & { responseType?: 'json' },
): Promise<T>;
function putFormData<T>(
	url: string,
	body: FormData,
	options?: Options & { responseType?: 'json' | 'file' },
): Promise<T | FileResponse> {
	const headers: Record<string, string> = {
		'Accept-Language': 'fr',
		...options?.headers,
	};

	if (options?.responseType !== 'file') {
		headers.accept = 'application/json';
	}

	const init: RequestInit = {
		method: 'PUT',
		credentials: 'include',
		headers,
		body,
	};

	if (options?.responseType === 'file') {
		return callFile(url, init, options);
	}

	return call(url, init, options);
}

const patch = <T>(url: string, body: Record<string, unknown>, options?: Options): Promise<T> => {
	return call(
		url,
		{
			method: 'PATCH',
			credentials: 'include',
			headers: {
				'Accept-Language': 'fr',
				'Content-Type': 'application/json',
				accept: 'application/json',
				...options?.headers,
			},
			body: JSON.stringify(body),
		},
		options,
	);
};

const put = <T>(url: string, body: Record<string, unknown>, options?: Options): Promise<T> => {
	return call(
		url,
		{
			method: 'PUT',
			credentials: 'include',
			headers: {
				'Accept-Language': 'fr',
				'Content-Type': 'application/json',
				accept: 'application/json',
				...options?.headers,
			},
			body: JSON.stringify(body),
		},
		options,
	);
};

const del = <T>(url: string, options?: Options): Promise<T> => {
	return call(
		url,
		{
			method: 'DELETE',
			credentials: 'include',
			headers: {
				'Accept-Language': 'fr',
				'Content-Type': 'application/json',
				accept: 'application/json',
				...options?.headers,
			},
		},
		options,
	);
};

const needRefreshToken = (response: Response): boolean => response.status === 401;

/**
 * A 401 body is always empty (the status code is the whole answer, naming the reason would turn it
 * into an oracle): its body is never parsed. Any other error body is parsed defensively — a 500
 * behind a proxy can answer HTML, and a parse failure must not replace a clear status with a thrown
 * `SyntaxError`.
 */
const parseErrorBody = async (response: Response): Promise<Record<string, unknown> | undefined> => {
	if (response.status === 401) {
		return undefined;
	}

	try {
		return await response.json();
	} catch {
		return undefined;
	}
};

const sessionLostListeners = new Set<() => void>();

/** Registers a listener called when a request is still answered 401 after a token refresh. Returns the unsubscribe function. */
const onSessionLost = (listener: () => void): (() => void) => {
	sessionLostListeners.add(listener);

	return () => {
		sessionLostListeners.delete(listener);
	};
};

const notifySessionLost = (): void => {
	for (const listener of [...sessionLostListeners]) {
		try {
			listener();
		} catch {
			// A failing listener must neither prevent the others from running nor mask the ApiError.
		}
	}
};

const call = async <T>(info: RequestInfo, init: RequestInit, options?: Options): Promise<T> => {
	const response = await Fetcher.concurrencyFetch(info, init);

	if (!response.ok) {
		if (needRefreshToken(response) && options?.retryOnUnauthorized !== false) {
			await refreshToken();

			const secondResponse = await Fetcher.concurrencyFetch(info, {
				...init,
				headers: { ...init.headers },
			});

			if (!secondResponse.ok) {
				if (secondResponse.status === 401) {
					notifySessionLost();
				}

				throw new ApiError(secondResponse.statusText, {
					status: secondResponse.status,
					body: await parseErrorBody(secondResponse),
				});
			}

			if (secondResponse.status === 204) {
				return undefined as unknown as T;
			}

			return secondResponse.json();
		}

		throw new ApiError(response.statusText, {
			status: response.status,
			body: await parseErrorBody(response),
		});
	}

	if (response.status === 204) {
		return undefined as unknown as T;
	}

	return await response.json();
};

const extractFileFromResponse = async (response: Response): Promise<FileResponse> => {
	const blob = await response.blob();
	const contentDisposition = response.headers.get('Content-Disposition');
	const filename = contentDisposition?.match(/filename=["']?([^"';\s]+)["']?/)?.[1] ?? null;

	return { blob, filename };
};

const callFile = async (info: RequestInfo, init: RequestInit, options?: Options): Promise<FileResponse> => {
	const response = await Fetcher.concurrencyFetch(info, init);

	if (!response.ok) {
		if (needRefreshToken(response) && options?.retryOnUnauthorized !== false) {
			await refreshToken();

			const secondResponse = await Fetcher.concurrencyFetch(info, {
				...init,
				headers: { ...init.headers },
			});

			if (!secondResponse.ok) {
				if (secondResponse.status === 401) {
					notifySessionLost();
				}

				throw new ApiError(secondResponse.statusText, {
					status: secondResponse.status,
					body: await parseErrorBody(secondResponse),
				});
			}

			return extractFileFromResponse(secondResponse);
		}

		throw new ApiError(response.statusText, {
			status: response.status,
			body: await parseErrorBody(response),
		});
	}

	return extractFileFromResponse(response);
};

export {
	onSessionLost,
	notifySessionLost,
	get,
	getPaginated,
	getFile,
	postFile,
	post,
	postFormData,
	put,
	putFormData,
	patch,
	del,
};
